import { Directive, ElementRef, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/** Propriedades do `vivest-select` (design system) usadas pela integração com formulários. */
type VivestSelectElement = HTMLElement & { value: string; disabled: boolean };

/**
 * Liga o `<vivest-select>` do design system aos formulários do Angular (`formControlName`,
 * `formControl` e `ngModel`). O web component expõe `value`/`disabled` como propriedades e
 * dispara `change` ao escolher uma opção, mas não implementa `ControlValueAccessor`.
 *
 * @example
 * <vivest-select label="Provedor" [options]="opcoes" formControlName="provedor"></vivest-select>
 */
@Directive({
  selector: 'vivest-select[formControlName], vivest-select[formControl], vivest-select[ngModel]',
  standalone: true,
  host: {
    '(change)': 'aoAlterar()',
    '(focusout)': 'aoSair()',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VivestSelectValueAccessorDirective),
      multi: true,
    },
  ],
})
export class VivestSelectValueAccessorDirective implements ControlValueAccessor {
  private readonly elemento = inject<ElementRef<VivestSelectElement>>(ElementRef).nativeElement;

  private onChange: (valor: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(valor: string | null): void {
    this.elemento.value = valor ?? '';
  }

  registerOnChange(fn: (valor: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(desabilitado: boolean): void {
    this.elemento.disabled = desabilitado;
  }

  aoAlterar(): void {
    this.onChange(this.elemento.value);
  }

  aoSair(): void {
    this.onTouched();
  }
}
